"use client"

import { FormEvent, useEffect, useMemo, useState } from "react"
import Link from "next/link"

const money = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
})

const inputStyle = {
  width: "100%",
  border: "1px solid var(--border)",
  borderRadius: 4,
  background: "var(--bg)",
  color: "var(--text)",
  padding: "11px 12px",
  fontSize: 13,
  outline: "none",
} as const

const labelStyle = {
  display: "block",
  fontSize: 11,
  color: "var(--muted)",
  marginBottom: 7,
} as const

function toNumber(value: string) {
  if (!value.trim()) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

function optionalNumber(value: string) {
  if (!value.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

function trackingEvent(event: string, details: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return
  const dataLayer = (window as any).dataLayer
  if (Array.isArray(dataLayer)) dataLayer.push({ event, ...details })
}

export function VMwareAssessmentFunnel() {
  const [vmwareAnnual, setVmwareAnnual] = useState("")
  const [proxmoxAnnual, setProxmoxAnnual] = useState("")
  const [migrationOneOff, setMigrationOneOff] = useState("")
  const [changeOneOff, setChangeOneOff] = useState("")
  const [years, setYears] = useState("3")

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    hosts: "",
    vms: "",
    vmwareVersion: "",
    renewalDate: "",
    renewalQuote: "",
    interest: "assessment",
    dependencies: "",
    notes: "",
    consent: false,
    website: "",
  })
  const [attribution, setAttribution] = useState<Record<string, string>>({})
  const [submitState, setSubmitState] = useState<"idle" | "sending" | "success" | "error">("idle")
  const [submitMessage, setSubmitMessage] = useState("")

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"]
    const values: Record<string, string> = {}
    keys.forEach((key) => {
      const value = params.get(key)
      if (value) values[key] = value
    })
    setAttribution(values)
  }, [])

  const model = useMemo(() => {
    const horizon = Math.min(5, Math.max(1, Number(years) || 3))
    const vmware = toNumber(vmwareAnnual)
    const proxmox = toNumber(proxmoxAnnual)
    const migration = toNumber(migrationOneOff)
    const changes = toNumber(changeOneOff)
    const oneOff = migration + changes
    const vmwareTotal = vmware * horizon
    const alternativeTotal = proxmox * horizon + oneOff
    const difference = vmwareTotal - alternativeTotal
    const annualSaving = vmware - proxmox
    const breakEvenMonths = annualSaving > 0 ? (oneOff / annualSaving) * 12 : null

    return {
      years: horizon,
      vmwareAnnual: vmware,
      proxmoxAnnual: proxmox,
      migrationOneOff: migration,
      changeOneOff: changes,
      vmwareTotal,
      alternativeTotal,
      difference,
      breakEvenMonths,
    }
  }, [vmwareAnnual, proxmoxAnnual, migrationOneOff, changeOneOff, years])

  const hasModel = vmwareAnnual.trim() !== "" && proxmoxAnnual.trim() !== ""

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  function attachModel() {
    if (vmwareAnnual.trim() && !form.renewalQuote.trim()) {
      updateForm("renewalQuote", vmwareAnnual)
    }
    trackingEvent("vmware_tco_assessment_click", {
      horizon_years: model.years,
      model_completed: hasModel,
    })
    document.getElementById("vmware-assessment-form")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitState === "sending") return

    setSubmitState("sending")
    setSubmitMessage("")

    try {
      const response = await fetch("/api/vmware-assessment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          hosts: optionalNumber(form.hosts),
          vms: optionalNumber(form.vms),
          renewalQuote: optionalNumber(form.renewalQuote),
          calculator: hasModel ? model : {},
          attribution,
        }),
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.success) {
        throw new Error(data.error || "We could not send the assessment request.")
      }

      setSubmitState("success")
      setSubmitMessage("Thanks — your VMware renewal assessment request has been sent to HSVE. We’ll reply by email.")
      trackingEvent("vmware_assessment_submit", {
        interest: form.interest,
        model_completed: hasModel,
        utm_source: attribution.utm_source || "",
        utm_campaign: attribution.utm_campaign || "",
      })
    } catch (error) {
      setSubmitState("error")
      setSubmitMessage(error instanceof Error ? error.message : "We could not send the assessment request.")
    }
  }

  const differenceText = model.difference >= 0
    ? `${money.format(model.difference)} lower over ${model.years} years`
    : `${money.format(Math.abs(model.difference))} higher over ${model.years} years`

  return (
    <section
      id="renewal-assessment"
      className="page-padding"
      style={{ padding: "68px 48px", borderBottom: "1px solid var(--border-soft)", scrollMarginTop: 76 }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div className="font-mono" style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.08em", marginBottom: 14 }}>
          # renewal-assessment
        </div>
        <h2 style={{ fontSize: 42, fontWeight: 600, letterSpacing: "-0.03em", margin: 0 }}>
          Model the renewal. Then send us the estate.
        </h2>
        <p style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.65, maxWidth: 860, margin: "12px 0 34px" }}>
          Use your own commercial assumptions to build an indicative comparison, then attach those numbers to a VMware Renewal Assessment. HSVE will review the technical fit separately before recommending any move.
        </p>

        <div className="responsive-grid" style={{ display: "grid", gridTemplateColumns: "0.95fr 1.05fr", gap: 16, alignItems: "start" }}>
          <div style={{ border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg-soft)", padding: 26 }}>
            <div className="font-mono" style={{ fontSize: 11, color: "var(--accent)", marginBottom: 18 }}>
              $ tco --compare=vmware,proxmox
            </div>
            <h3 style={{ fontSize: 24, letterSpacing: "-0.02em", margin: 0 }}>VMware → Proxmox TCO model</h3>
            <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6, margin: "9px 0 22px" }}>
              No vendor pricing is assumed. Enter the figures that apply to your organisation. Blank one-off fields are treated as £0.
            </p>

            <div className="responsive-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label>
                <span className="font-mono" style={labelStyle}>Annual VMware renewal / support (£)</span>
                <input type="number" min="0" max="100000000" step="1" inputMode="decimal" value={vmwareAnnual} onChange={(e) => setVmwareAnnual(e.target.value)} style={inputStyle} placeholder="e.g. 18000" />
              </label>
              <label>
                <span className="font-mono" style={labelStyle}>Annual Proxmox / support assumption (£)</span>
                <input type="number" min="0" max="100000000" step="1" inputMode="decimal" value={proxmoxAnnual} onChange={(e) => setProxmoxAnnual(e.target.value)} style={inputStyle} placeholder="e.g. 4500" />
              </label>
              <label>
                <span className="font-mono" style={labelStyle}>One-off migration + internal effort (£)</span>
                <input type="number" min="0" max="100000000" step="1" inputMode="decimal" value={migrationOneOff} onChange={(e) => setMigrationOneOff(e.target.value)} style={inputStyle} placeholder="e.g. 8000" />
              </label>
              <label>
                <span className="font-mono" style={labelStyle}>Other one-off changes (£)</span>
                <input type="number" min="0" max="100000000" step="1" inputMode="decimal" value={changeOneOff} onChange={(e) => setChangeOneOff(e.target.value)} style={inputStyle} placeholder="hardware, backup, network…" />
              </label>
            </div>

            <label style={{ display: "block", marginTop: 12 }}>
              <span className="font-mono" style={labelStyle}>Comparison horizon</span>
              <select value={years} onChange={(e) => setYears(e.target.value)} style={inputStyle}>
                <option value="1">1 year</option>
                <option value="3">3 years</option>
                <option value="5">5 years</option>
              </select>
            </label>

            <div style={{ marginTop: 20, border: "1px solid var(--border)", borderRadius: 5, overflow: "hidden", background: "var(--bg)" }}>
              {hasModel ? (
                <>
                  {[
                    [`${model.years}-year VMware scenario`, money.format(model.vmwareTotal)],
                    [`${model.years}-year alternative scenario`, money.format(model.alternativeTotal)],
                    ["Indicative difference", differenceText],
                    ["Indicative break-even", model.breakEvenMonths === null ? "No break-even on these annual inputs" : `${model.breakEvenMonths.toFixed(1)} months`],
                  ].map(([label, value], index) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 20, padding: "12px 14px", borderTop: index ? "1px solid var(--border-soft)" : "none", alignItems: "baseline" }}>
                      <span style={{ fontSize: 12, color: "var(--text-2)" }}>{label}</span>
                      <strong className="font-mono" style={{ fontSize: 12, textAlign: "right" }}>{value}</strong>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ padding: 16, fontSize: 13, color: "var(--muted)", lineHeight: 1.55 }}>
                  Enter annual VMware and Proxmox/support figures to calculate the comparison.
                </div>
              )}
            </div>

            <p style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.55, margin: "14px 0 0" }}>
              Indicative planning model only. It excludes tax and any costs you do not enter, and it is not an HSVE quote, savings guarantee or recommendation.
            </p>

            <button
              type="button"
              onClick={attachModel}
              className="font-mono"
              style={{ marginTop: 18, border: 0, borderRadius: 4, background: "var(--accent)", color: "var(--accent-ink)", padding: "11px 15px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              Use these numbers in my assessment ↓
            </button>
          </div>

          <div id="vmware-assessment-form" style={{ border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg)", padding: 26, scrollMarginTop: 76 }}>
            <div className="font-mono" style={{ fontSize: 11, color: "var(--accent)", marginBottom: 18 }}>
              $ request renewal-assessment
            </div>
            <h3 style={{ fontSize: 24, letterSpacing: "-0.02em", margin: 0 }}>Request a VMware Renewal Assessment</h3>
            <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6, margin: "9px 0 22px" }}>
              Give us enough to understand the size and timing of the decision. The calculator snapshot is attached automatically if you completed it.
            </p>

            {submitState === "success" ? (
              <div role="status" aria-live="polite" style={{ border: "1px solid var(--border)", borderRadius: 5, background: "var(--bg-soft)", padding: 22 }}>
                <div className="font-mono" style={{ color: "var(--success)", fontSize: 12 }}>✓ assessment_request_sent</div>
                <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.65, margin: "10px 0 0" }}>{submitMessage}</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="responsive-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <label>
                    <span className="font-mono" style={labelStyle}>Name *</span>
                    <input required maxLength={100} autoComplete="name" value={form.name} onChange={(e) => updateForm("name", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>Work email *</span>
                    <input required type="email" maxLength={160} autoComplete="email" value={form.email} onChange={(e) => updateForm("email", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>Company *</span>
                    <input required maxLength={140} autoComplete="organization" value={form.company} onChange={(e) => updateForm("company", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>Role</span>
                    <input maxLength={100} autoComplete="organization-title" value={form.role} onChange={(e) => updateForm("role", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>VMware hosts</span>
                    <input type="number" min="0" max="1000" step="1" inputMode="numeric" value={form.hosts} onChange={(e) => updateForm("hosts", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>Approx. VM count</span>
                    <input type="number" min="0" max="100000" step="1" inputMode="numeric" value={form.vms} onChange={(e) => updateForm("vms", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>VMware product / version</span>
                    <input maxLength={140} value={form.vmwareVersion} onChange={(e) => updateForm("vmwareVersion", e.target.value)} style={inputStyle} placeholder="e.g. vSphere 8 / ESXi 8" />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>Renewal due</span>
                    <input type="month" value={form.renewalDate} onChange={(e) => updateForm("renewalDate", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>Annual renewal quote (£)</span>
                    <input type="number" min="0" max="100000000" step="1" inputMode="decimal" value={form.renewalQuote} onChange={(e) => updateForm("renewalQuote", e.target.value)} style={inputStyle} />
                  </label>
                  <label>
                    <span className="font-mono" style={labelStyle}>What do you need? *</span>
                    <select required value={form.interest} onChange={(e) => updateForm("interest", e.target.value)} style={inputStyle}>
                      <option value="assessment">Assessment only</option>
                      <option value="assessment-and-migration">Assessment + migration support</option>
                      <option value="planning">Early-stage planning</option>
                      <option value="not-sure">Not sure yet</option>
                    </select>
                  </label>
                </div>

                <label style={{ display: "block", marginTop: 12 }}>
                  <span className="font-mono" style={labelStyle}>Known VMware-specific dependencies</span>
                  <textarea maxLength={2500} rows={4} value={form.dependencies} onChange={(e) => updateForm("dependencies", e.target.value)} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} placeholder="vSAN, NSX, backup tooling, vendor support requirements, automation…" />
                </label>

                <label style={{ display: "block", marginTop: 12 }}>
                  <span className="font-mono" style={labelStyle}>Anything else we should know?</span>
                  <textarea maxLength={3500} rows={4} value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} placeholder="Deadline, maintenance windows, hardware plans, business constraints…" />
                </label>

                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                  <label>
                    Website
                    <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => updateForm("website", e.target.value)} />
                  </label>
                </div>

                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, fontSize: 12, color: "var(--text-2)", lineHeight: 1.55 }}>
                  <input required type="checkbox" checked={form.consent} onChange={(e) => updateForm("consent", e.target.checked)} style={{ marginTop: 3 }} />
                  <span>
                    I agree that HSVE can use these details to respond to this enquiry. See the <Link href="/privacy" style={{ color: "var(--text)", textDecoration: "underline" }}>privacy policy</Link>.
                  </span>
                </label>

                {submitState === "error" && (
                  <p role="alert" aria-live="polite" style={{ fontSize: 12, color: "var(--accent)", lineHeight: 1.55, margin: "14px 0 0" }}>
                    {submitMessage} You can also email support@hsve.co.uk.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitState === "sending"}
                  className="font-mono"
                  style={{ marginTop: 18, border: 0, borderRadius: 4, background: "var(--accent)", color: "var(--accent-ink)", padding: "12px 16px", fontSize: 12, fontWeight: 600, cursor: submitState === "sending" ? "wait" : "pointer", opacity: submitState === "sending" ? 0.7 : 1 }}
                >
                  {submitState === "sending" ? "Sending assessment…" : "Request my VMware assessment →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
